import React, { useState, useEffect } from 'react';
import { getFieldSections } from '../utils/productFieldTemplates';

const SelectStep = ({ title, items = [], onSelect, selectedId, placeholder }) => (
  <div className="mb-8">
    <h2 className="text-xl font-semibold text-gray-900 mb-4">{title}</h2>
    <select
      value={selectedId || ''}
      onChange={(e) => {
        const selected = items.find(item => item.id === parseInt(e.target.value));
        onSelect(selected);
      }}
      className="w-full p-2 border rounded-lg"
    >
      <option value="">{placeholder}</option>
      {items.map(item => (
        <option key={item.id} value={item.id}>
          {item.name} {item.subcategories && `(${item.subcategories.length} subcategorias)`}
        </option>
      ))}
    </select>
  </div>
);

const ProductForm = ({ subcategory, category, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    price_usd: '',
    especificacoes: {}
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load field sections when category or subcategory changes
  useEffect(() => {
    const loadSections = async () => {
      if (subcategory?.id) {
        // Load merged template when subcategory is selected
        setLoading(true);
        try {
          const fieldSections = await getFieldSections(category?.name?.toLowerCase(), subcategory.id);
          setSections(fieldSections);
        } catch (error) {
          console.error('Failed to load field sections:', error);
          setSections([]);
        } finally {
          setLoading(false);
        }
      } else if (category?.name) {
        // Fall back to category-only template
        setLoading(true);
        try {
          const fieldSections = await getFieldSections(category.name.toLowerCase());
          setSections(fieldSections);
        } catch (error) {
          console.error('Failed to load field sections:', error);
          setSections([]);
        } finally {
          setLoading(false);
        }
      } else {
        setSections([]);
      }
    };

    loadSections();
  }, [category?.name, subcategory?.id]);

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages(prev => [...prev, ...files]);

    // Create preview URLs
    const newPreviews = files.map(file => ({
      url: URL.createObjectURL(file),
      name: file.name
    }));
    setImagePreviews(prev => [...prev, ...newPreviews]);
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => {
      // Revoke the URL to free memory
      URL.revokeObjectURL(prev[index].url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Check if the filter field is filled
    const filterField = getFilterField();
    if (filterField) {
      const [parent, child] = filterField.key.split('.');
      const value = child 
        ? formData.especificacoes[parent]?.[child]
        : formData.especificacoes[filterField.key];
      
      if (!value) {
        alert(`O campo "${filterField.label}" é obrigatório para este tipo de produto.`);
        return;
      }
    }

    onSubmit({
      ...formData,
      subcategory_id: subcategory.id,
      images: selectedImages
    });
  };

  // Helper to get the filter field based on subcategory.filter_type
  const getFilterField = () => {
    switch (subcategory.filter_type) {
      case 'potencia':
        return { key: 'potencia_kw', label: 'Potência (kW)' };
      case 'espessura':
        return { key: 'espessura_mm', label: 'Espessura (mm)' };
      case 'capacidade':
        return { key: 'capacidade_ton', label: 'Capacidade (Ton)' };
      default:
        return null;
    }
  };

  // Helper to check if a field is required
  const isFieldRequired = (key) => {
    const filterField = getFilterField();
    return filterField?.key === key;
  };

  const filterField = getFilterField();

  const handleNestedChange = (key, value) => {
    const [parent, child] = key.split('.');
    if (child) {
      setFormData({
        ...formData,
        especificacoes: {
          ...formData.especificacoes,
          [parent]: {
            ...(formData.especificacoes[parent] || {}),
            [child]: value
          }
        }
      });
    } else {
      setFormData({
        ...formData,
        especificacoes: {
          ...formData.especificacoes,
          [key]: value
        }
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-xl font-semibold mb-6">Novo Modelo</h3>
      
      <div className="space-y-6">
        {/* Basic Info */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-4">Informações Básicas</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome do Modelo
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-md border-orange-200"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preço (USD)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.price_usd}
                onChange={e => setFormData({ ...formData, price_usd: e.target.value })}
                className="w-full px-3 py-2 border rounded-md border-orange-200"
                required
              />
            </div>
          </div>
        </div>

        {/* Specifications by Section */}
        {loading ? (
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-center text-gray-500">
              Carregando campos de especificação...
            </div>
          </div>
        ) : sections.map(section => (
          <div key={section.title} className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-4">{section.title}</h4>
            <div className="space-y-4">
              {section.fields.map(({ key, label }) => {
                const required = isFieldRequired(key);
                return (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {label}
                      {required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <input
                      type="text"
                      value={key.includes('.') 
                        ? formData.especificacoes[key.split('.')[0]]?.[key.split('.')[1]] || ''
                        : formData.especificacoes[key] || ''}
                      onChange={e => handleNestedChange(key, e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md ${required ? 'border-orange-200' : ''}`}
                      required={required}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Common Fields */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-4">Dimensões e Peso</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dimensões (mm)
              </label>
              <input
                type="text"
                value={formData.especificacoes.dimensoes_mm || ''}
                onChange={e => handleNestedChange('dimensoes_mm', e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Peso (kg)
              </label>
              <input
                type="text"
                value={formData.especificacoes.peso_kg || ''}
                onChange={e => handleNestedChange('peso_kg', e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          </div>
        </div>

        {/* Image Upload Section */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-4">Imagens do Produto</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adicionar Imagens
              </label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageSelect}
                className="w-full px-3 py-2 border rounded-md"
              />
              <p className="text-sm text-gray-500 mt-1">Selecione múltiplas imagens para criar um carrossel</p>
            </div>

            {/* Image Previews */}
            {imagePreviews.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Imagens selecionadas:</p>
                <div className="grid grid-cols-3 gap-4">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="relative">
                      <img
                        src={preview.url}
                        alt={preview.name}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      <p className="text-xs text-gray-600 truncate mt-1">{preview.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <button
          type="submit"
          className="w-full bg-orange-500 text-white py-2 px-4 rounded-md hover:bg-orange-600 transition-colors"
        >
          Criar Modelo
        </button>
      </div>
    </form>
  );
};

const ProductModelManager = ({ categoriesData }) => {
  console.log('Categories data string received:', categoriesData);
  const categories = categoriesData ? JSON.parse(categoriesData) : [];
  console.log('Parsed categories:', categories);
  
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedParentSub, setSelectedParentSub] = useState(null);
  const [selectedLeafSub, setSelectedLeafSub] = useState(null);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSelectedParentSub(null);
    setSelectedLeafSub(null);
  };

  const handleParentSubSelect = (subcategory) => {
    setSelectedParentSub(subcategory);
    // If this subcategory has no children (is a leaf), select it directly
    if (!subcategory.child_subcategories?.length) {
      setSelectedLeafSub(subcategory);
    } else {
      setSelectedLeafSub(null);
    }
  };

  const handleLeafSubSelect = (subcategory) => {
    setSelectedLeafSub(subcategory);
  };

  const handleSubmit = async (formData) => {
    try {
      // Create FormData object for multipart form submission
      const form = new FormData();
      
      // Add basic fields
      form.append('product_model[name]', formData.name);
      form.append('product_model[price_usd]', formData.price_usd);
      form.append('product_model[subcategory_id]', formData.subcategory_id);
      
      // Add especificacoes as JSON
      form.append('product_model[especificacoes]', JSON.stringify(formData.especificacoes));
      
      // Add images
      if (formData.images && formData.images.length > 0) {
        formData.images.forEach((image) => {
          form.append('product_model[images][]', image);
        });
      }

      const response = await fetch('/admin/product_models', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: form
      });

      if (!response.ok && !response.redirected) throw new Error('Falha ao criar produto');

      // Reset form and selections on success
      setSelectedCategory(null);
      setSelectedParentSub(null);
      setSelectedLeafSub(null);
      
      alert('Produto criado com sucesso!');
    } catch (error) {
      console.error('Erro ao criar produto:', error);
      alert('Erro ao criar produto. Por favor, tente novamente.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Gerenciar Produtos
          </h1>
          <p className="text-gray-600">
            Selecione a categoria e subcategoria para criar um novo modelo
          </p>
        </div>

        {/* Selection Steps */}
        <SelectStep
          title="1. Selecione a Categoria"
          items={categories}
          selectedId={selectedCategory?.id}
          onSelect={handleCategorySelect}
          placeholder="Escolha uma categoria..."
        />

        {selectedCategory && (
          <SelectStep
            title="2. Selecione a Subcategoria"
            items={selectedCategory.subcategories}
            selectedId={selectedParentSub?.id}
            onSelect={handleParentSubSelect}
            placeholder="Escolha uma subcategoria..."
          />
        )}

        {selectedParentSub && selectedParentSub.child_subcategories?.length > 0 && (
          <SelectStep
            title="3. Selecione o Tipo Específico"
            items={selectedParentSub.child_subcategories}
            selectedId={selectedLeafSub?.id}
            onSelect={handleLeafSubSelect}
            placeholder="Escolha um tipo específico..."
          />
        )}

        {/* Product Form */}
        {selectedLeafSub && (
          <ProductForm
            subcategory={selectedLeafSub}
            category={selectedCategory}
            onSubmit={handleSubmit}
          />
        )}
      </div>
    </div>
  );
};

export default ProductModelManager; 